<template>
<div class="main-content">
  <!-- Toast 组件 -->
  <div v-if="toast.show" class="toast-container" :class="toast.type">
    <div class="toast-content">
      <span class="toast-icon">{{ toast.icon }}</span>
      <span class="toast-message">{{ toast.message }}</span>
    </div>
  </div>

  <!-- 费用确认弹窗 -->
  <div v-if="showConfirmModal" class="modal-overlay" @click.self="closeConfirmModal">
    <div class="modal-container">
      <div class="modal-header">
        <h3 class="modal-title">费用确认</h3>
        <button class="modal-close" @click="closeConfirmModal">×</button>
      </div>
      <div class="modal-body">
        <div class="cost-detail">
          <div v-for="(item, index) in getCostBreakdown()" :key="index" class="cost-item">
            <span class="service-name">{{ item.name }}</span>
            <span class="service-price">{{ item.price }}元</span>
          </div>
          <div class="cost-total">
            <span>总计:</span>
            <span class="total-amount">{{ calculatePrice() }}元</span>
          </div>
        </div>
        <p class="modal-tip">确认后将扣除相应余额，是否继续？</p>
      </div>
      <div class="modal-footer">
        <button class="modal-btn modal-btn-cancel" @click="handleCancelConfirm">取消</button>
        <button class="modal-btn modal-btn-confirm" @click="confirmProcessing">确认处理</button>
      </div>
    </div>
  </div>

  <div class="audio-processing-container">
    <h1 class="title">音频处理</h1>
    <div class="content-flex">
      <!-- 单列区域 -->
      <div class="main-area">
        <!-- 音频上传区域 -->
        <div
          class="audio-upload-area"
          @dragover.prevent
          @drop.prevent="handleFileDrop"
          @click="triggerFileUpload"
          @mouseenter="showDeleteButton = true"
          @mouseleave="handleMouseLeave"
        >
          <input type="file" ref="fileInput" class="hidden-input" accept="audio/*" @change="handleFileSelect" />
          <!-- 删除按钮 -->
          <button
            v-if="showDeleteButton && selectedFile"
            @click.stop="handleDeleteClick"
            class="delete-button"
            :class="{ 'clicked': deleteButtonClicked }"
            aria-label="删除音频文件"
          >
            ×
          </button>
          <template v-if="!selectedFile">
            <div class="upload-icon">+</div>
            <p class="upload-text">点击上传或拖拽音频</p>
            <p class="upload-subtext">支持mp3、wav、ogg、aac格式,大小不超过50MB</p>
          </template>
          <template v-else>
            <div class="audio-info">
              <div class="file-info">
                <span class="file-name" :title="selectedFile.name">{{ truncateFileName(selectedFile.name) }}</span>
                <div class="time-info">
                  <span>{{ formatTime(currentTime) }}</span>
                  <span>/ {{ formatTime(audioDuration) }}</span>
                </div>
              </div>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: `${(currentTime / audioDuration) * 100}%` }"></div>
              </div>
              <div class="play-controls">
                <button @click.stop="togglePlay" class="play-button">
                  <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                  {{ isPlaying ? '暂停' : '播放' }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <!-- 功能类型选择器 -->
        <div class="function-selector">
          <label class="selector-label">选择处理功能:</label>
          <select v-model="selectedFunction" class="function-select">
            <option value="subtitle_generation">生成音频字幕</option>
            <option value="voice_separation">提取人声去除噪音</option>
            <option value="voice_clone_translation">音频原语音克隆翻译</option>
          </select>
        </div>

        <!-- 生成音频字幕功能 -->
        <template v-if="selectedFunction === 'subtitle_generation'">
          <div class="feature-section">
            <div class="feature-content no-border">
              <!-- 字幕语言选择 -->
              <div class="option-row">
                <div class="option-half">返回的字幕语言为:</div>
                <div class="option-half">
                  <select v-model="subtitle_language_type" class="option-select">
                    <option value="original">原语言字幕</option>
                    <option value="target">翻译后字幕</option>
                    <option value="bilingual">双语字幕</option>
                  </select>
                </div>
              </div>

              <!-- 目标语言选择（去掉复选框，直接显示） -->
              <div class="option-row">
                <div class="option-half">将字幕翻译成：</div>
                <div class="option-half">
                  <select v-model="target_language" class="option-select">
                    <option value="zh">中文 (Chinese)</option>
                    <option value="en">英语 (English)</option>
                    <option value="de">德语 (German)</option>
                    <option value="fr">法语 (French)</option>
                    <option value="ru">俄语 (Russian)</option>
                    <option value="ja">日语 (Japanese)</option>
                    <option value="ko">韩语 (Korean)</option>
                    <option value="es">西班牙语 (Spanish)</option>
                    <option value="pt">葡萄牙语 (Portuguese)</option>
                    <option value="ar">阿拉伯语 (Arabic)</option>
                  </select>
                </div>
              </div>

              <div class="option-row">
                <div class="option-half">字幕文件格式:</div>
                <div class="option-half">
                  <select v-model="subtitle_format" class="option-select">
                    <option value="srt">SRT</option>
                    <option value="ass">ASS</option>
                    <option value="ssa">SSA</option>
                    <option value="vtt">VTT</option>
                    <option value="ttml">TTML</option>
                    <option value="mpl2">MPL2</option>
                    <option value="microdvd">MicroDVD</option>
                    <option value="sbv">SBV</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 人声分离功能 -->
        <template v-if="selectedFunction === 'voice_separation'">
          <!-- 音频截取 -->
          <div class="feature-section">
            <div class="feature-header" @click="toggleSection('audioCut')">
              <div class="feature-title">
                <input type="checkbox" v-model="audioCutEnabled" class="feature-checkbox" @click.stop />
                <span>音频截取</span>
              </div>
              <!-- 下拉三角箭头（使用 Unicode 字符，不依赖 Font Awesome） -->
              <span class="toggle-arrow">{{ sections.audioCut && audioCutEnabled ? '▲' : '▼' }}</span>
            </div>
            <div v-if="sections.audioCut && audioCutEnabled" class="feature-content">
              <div class="option-row">
                <div class="option-half">开始时间:</div>
                <div class="option-half option-relative">
                  <input type="text" v-model="cut_start_time" @input="validateTime('start')" class="option-input" placeholder="00:00:00:000" />
                  <span v-if="!isStartTimeValid" class="error-message">{{ startTimeError }}</span>
                </div>
              </div>
              <div class="option-row">
                <div class="option-half">结束时间:</div>
                <div class="option-half option-relative">
                  <input type="text" v-model="cut_end_time" @input="validateTime('end')" class="option-input" placeholder="00:00:00:000" />
                  <span v-if="!isEndTimeValid" class="error-message">{{ endTimeError }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 输出音频文件类型 -->
          <div class="feature-section">
            <div class="feature-content no-border">
              <div class="option-row">
                <div class="option-half">输出音频文件类型:</div>
                <div class="option-half">
                  <select v-model="output_format" class="option-select">
                    <option value="mp3">MP3</option>
                    <option value="wav">WAV</option>
                    <option value="ogg">OGG</option>
                    <option value="aac">AAC</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 音频原语音克隆翻译功能 -->
        <template v-if="selectedFunction === 'voice_clone_translation'">
          <div class="feature-section">
            <div class="feature-content no-border">
              <!-- 翻译的目标语言选择 -->
              <div class="option-row">
                <div class="option-half">翻译的目标语言:</div>
                <div class="option-half">
                  <select v-model="clone_target_language" class="option-select">
                    <option
                      v-for="item in audioCloneTranslateLanguages"
                      :key="item.value"
                      :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 开始处理按钮 -->
        <button
          class="process-button"
          :class="{ 'disabled': !selectedFile || processing || isProcessed }"
          :disabled="!selectedFile || processing || isProcessed"
          @click="showCostConfirm"
        >
          {{ getProcessButtonText() }}
        </button>
        <p class="terms-text">
          仅对本人音频进行处理。您承诺您的素材内容为本人所有或已取得合法授权,并遵守《音频处理功能使用规范》
        </p>
      </div>
    </div>
  </div>

</div>
</template>

<script>
import { getUpQiniuToken, genSubtitle, voiceSeparation, audioVoiceCloneTranslation, uploadToQiniu } from '@/api/upfile'
import { QINIU_BASE_URL, audioCloneTranslateLanguages } from '@/api/constants'
import { mapGetters } from 'vuex'

export default {
  name: 'AudioProcessing',
  data () {
    return {
      processing: false,
      isProcessed: false,
      selectedFile: null,
      audioElement: null,
      isPlaying: false,
      currentTime: 0,
      audioDuration: 0,
      audioUrl: '',
      fileUrl: '',
      showDeleteButton: false,
      deleteButtonClicked: false,
      cut_start_time: '00:00:00:000',
      cut_end_time: '00:00:00:000',
      startTimeError: '',
      endTimeError: '',
      isStartTimeValid: true,
      isEndTimeValid: true,
      selectedFunction: 'subtitle_generation',

      // 字幕生成参数
      translate_sub: false,
      target_language: 'en',
      subtitle_format: 'srt',
      // 字幕语言类型：original(原语言)/target(目标语言)/bilingual(双语)
      subtitle_language_type: 'original',

      // 人声分离参数
      sections: {
        audioCut: false
      },
      audioCutEnabled: false,
      voice_separation: 'vocal',
      output_format: 'mp3',

      // 音频原语音克隆翻译参数
      clone_target_language: 'zh',
      audioCloneTranslateLanguages,

      audioEvents: null,

      // Toast 状态
      toast: {
        show: false,
        message: '',
        type: 'info',
        icon: 'ℹ️'
      },

      // 费用确认弹窗控制
      showConfirmModal: false
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'balance']),
    hasEnabledFeatures () {
      return this.selectedFunction === 'subtitle_generation' ||
        this.selectedFunction === 'voice_separation' ||
        this.selectedFunction === 'voice_clone_translation'
    }
  },
  methods: {
    // 重置所有表单状态为初始值
    resetForm () {
      this.clearAudio()
      this.selectedFile = null
      this.fileUrl = ''
      this.showDeleteButton = false
      this.deleteButtonClicked = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.isPlaying = false
      this.currentTime = 0
      this.audioDuration = 0
      this.cut_start_time = '00:00:00:000'
      this.cut_end_time = '00:00:00:000'
      this.startTimeError = ''
      this.endTimeError = ''
      this.isStartTimeValid = true
      this.isEndTimeValid = true
      this.selectedFunction = 'subtitle_generation'
      this.translate_sub = false
      this.target_language = 'en'
      this.subtitle_format = 'srt'
      // 重置字幕语言类型
      this.subtitle_language_type = 'original'
      this.sections = { audioCut: false }
      this.audioCutEnabled = false
      this.voice_separation = 'vocal'
      this.output_format = 'mp3'
      this.clone_target_language = 'zh'
      this.isProcessed = false
      this.processing = false
    },

    closeConfirmModal () {
      this.showConfirmModal = false
    },

    handleCancelConfirm () {
      this.closeConfirmModal()
      this.resetForm()
      this.showToast('已取消操作', 'info')
    },

    showCostConfirm () {
      if (!this.isLoggedIn) {
        this.showToast('请先登录', 'warning')
        return
      }
      if (!this.selectedFile) {
        this.showToast('请先选择一个音频文件', 'warning')
        return
      }
      if (this.isProcessed) {
        this.showToast('当前文件已提交处理，请勿重复提交', 'warning')
        return
      }

      const estimatedCost = parseFloat(this.calculatePrice())
      if (this.balance < estimatedCost) {
        this.showToast('账户余额不足，请先充值', 'error')
        return
      }

      this.showConfirmModal = true
    },

    async confirmProcessing () {
      this.closeConfirmModal()
      this.processing = true
      const file = this.selectedFile

      try {
        const tokenResponse = await getUpQiniuToken()
        const qiniuUploadToken = tokenResponse.uploadToken

        const uploadFileName = this.generateQiniuFileName(file.name)
        const response = await uploadToQiniu(file, uploadFileName, qiniuUploadToken)
        this.fileUrl = `${QINIU_BASE_URL}/${response.data.key}`

        const baseData = {
          input_file_url: this.fileUrl,
          input_filename: file.name,
          audio_duration: this.audioDuration
        }

        let processResponse

        if (this.selectedFunction === 'subtitle_generation') {
          const requestData = {
            ...baseData,
            target_language: this.target_language,
            subtitle_format: this.subtitle_format,
            subtitle_language_type: this.subtitle_language_type
          }
          processResponse = await genSubtitle(requestData)
        } else if (this.selectedFunction === 'voice_separation') {
          const requestData = {
            ...baseData,
            output_format: this.output_format,
            cut_start_time: this.audioCutEnabled ? this.timeToSeconds(this.cut_start_time) : null,
            cut_end_time: this.audioCutEnabled ? this.timeToSeconds(this.cut_end_time) : null,
            voice_separation: 'vocal' // 固定发送 'vocal'，不依赖 this.voice_separation
          }
          processResponse = await voiceSeparation(requestData)
        } else if (this.selectedFunction === 'voice_clone_translation') {
          const requestData = {
            ...baseData,
            target_language: this.clone_target_language
          }
          processResponse = await audioVoiceCloneTranslation(requestData)
        }

        if (processResponse.status === 200) {
          this.showToast(processResponse.message || '处理已开始', 'success')
          if (typeof processResponse.balance !== 'undefined') {
            this.$store.commit('setBalance', processResponse.balance)
          }

          this.isProcessed = true
          this.resetForm()
        } else {
          this.showToast(processResponse.message || '处理失败', 'error')
        }
      } catch (error) {
        console.error(error)
        this.showToast('处理失败，请稍后重试', 'error')
      } finally {
        this.processing = false
      }
    },

    getProcessButtonText () {
      if (this.processing) {
        return '处理中...'
      }
      if (this.isProcessed) {
        return '处理完成'
      }
      return '开始处理'
    },

    generateQiniuFileName (originalFilename = 'audio.mp3') {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      const milliseconds = String(now.getMilliseconds()).padStart(3, '0')
      const randomMicro = Math.floor(Math.random() * 1000).toString().padStart(3, '0')
      const microSeconds = milliseconds + randomMicro
      let uuidShort
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        uuidShort = crypto.randomUUID().replace(/-/g, '').slice(0, 4)
      } else {
        uuidShort = Math.random().toString(16).substring(2, 6)
      }
      const ext = originalFilename.split('.').pop() || 'mp3'
      return `audioProcessing_${year}${month}${day}_${hours}${minutes}${seconds}_${microSeconds}_${uuidShort}.${ext}`
    },

    showToast (message, type = 'info', duration = 3000) {
      const icons = {
        success: '✓',
        error: '✕',
        warning: '⚠',
        info: 'ℹ️'
      }
      this.toast = {
        show: true,
        message,
        type,
        icon: icons[type] || icons.info
      }
      setTimeout(() => {
        this.toast.show = false
      }, duration)
    },

    calculatePrice () {
      if (!this.selectedFile || !this.audioDuration || this.audioDuration <= 0) {
        return '0.00'
      }
      const durationMinutes = this.audioDuration / 60
      let totalPrice = 0

      if (this.selectedFunction === 'subtitle_generation') {
        totalPrice += durationMinutes * 1.0
      }

      if (this.selectedFunction === 'voice_separation') {
        totalPrice += durationMinutes * 1.0
      }

      if (this.selectedFunction === 'voice_clone_translation') {
        totalPrice += durationMinutes * 2.0
      }

      return totalPrice.toFixed(2)
    },

    getCostBreakdown () {
      if (!this.selectedFile || !this.audioDuration || this.audioDuration <= 0) {
        return []
      }
      const durationMinutes = this.audioDuration / 60
      const breakdown = []

      if (this.selectedFunction === 'subtitle_generation') {
        breakdown.push({
          name: '音频转字幕',
          price: (durationMinutes * 1.0).toFixed(2)
        })
      }

      if (this.selectedFunction === 'voice_separation') {
        breakdown.push({
          name: '人声分离',
          price: (durationMinutes * 1.0).toFixed(2)
        })
      }

      if (this.selectedFunction === 'voice_clone_translation') {
        breakdown.push({
          name: '音频原语音克隆翻译',
          price: (durationMinutes * 2.0).toFixed(2)
        })
      }

      return breakdown
    },

    truncateFileName (name) {
      if (!name) return ''
      const maxWidth = 70
      if (name.length <= maxWidth) return name
      const lastDotIndex = name.lastIndexOf('.')
      const extension = lastDotIndex !== -1 ? name.substring(lastDotIndex) : ''
      const baseName = lastDotIndex !== -1 ? name.substring(0, lastDotIndex) : name
      const startLength = Math.floor((maxWidth - extension.length - 3) / 2)
      const endLength = maxWidth - startLength - 3 - extension.length
      return baseName.substring(0, startLength) + '...' + baseName.substring(baseName.length - endLength) + extension
    },

    triggerFileUpload () {
      if (!this.selectedFile) {
        this.$refs.fileInput.click()
      }
    },

    async handleFileSelect (event) {
      const file = event.target.files[0]
      if (file && file.type.startsWith('audio/')) {
        this.isProcessed = false
        await this.processAudioFile(file)
      } else {
        this.showToast('请选择有效的音频文件', 'error')
      }
    },

    async handleFileDrop (event) {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('audio/')) {
        this.isProcessed = false
        await this.processAudioFile(file)
      } else {
        this.showToast('请拖拽有效的音频文件', 'error')
      }
    },

    async processAudioFile (file) {
      this.clearAudioNoReset()
      this.selectedFile = file
      this.audioUrl = URL.createObjectURL(file)
      this.audioElement = new Audio(this.audioUrl)
      const onLoadedMetadata = () => {
        this.audioDuration = this.audioElement.duration || 0
        this.cut_end_time = this.formatTime(this.audioDuration)
        this.$nextTick(() => {
          this.validateTime('start')
          this.validateTime('end')
        })
      }
      const onTimeUpdate = () => {
        if (this.audioElement) {
          this.currentTime = this.audioElement.currentTime
        }
      }
      const onEnded = () => {
        this.isPlaying = false
        this.currentTime = 0
      }
      const onError = () => {
        if (this.audioElement && this.audioElement.error) {
          console.error('音频播放错误:', this.audioElement.error)
        }
        this.showToast('音频文件加载失败,请检查文件是否损坏', 'error')
        this.clearAudio()
      }
      this.audioEvents = { onLoadedMetadata, onTimeUpdate, onEnded, onError }
      this.audioElement.addEventListener('loadedmetadata', onLoadedMetadata)
      this.audioElement.addEventListener('timeupdate', onTimeUpdate)
      this.audioElement.addEventListener('ended', onEnded)
      this.audioElement.addEventListener('error', onError)
      this.isPlaying = false
      this.currentTime = 0
      this.audioDuration = 0
    },

    togglePlay (event) {
      event.stopPropagation()
      if (!this.audioElement) return
      if (this.isPlaying) {
        this.audioElement.pause()
      } else {
        this.audioElement.play().catch(err => {
          console.error('播放失败:', err)
          this.showToast('播放失败,请重试', 'error')
        })
      }
      this.isPlaying = !this.isPlaying
    },

    clearAudioNoReset () {
      if (this.audioElement) {
        const evts = this.audioEvents
        if (evts) {
          this.audioElement.removeEventListener('loadedmetadata', evts.onLoadedMetadata)
          this.audioElement.removeEventListener('timeupdate', evts.onTimeUpdate)
          this.audioElement.removeEventListener('ended', evts.onEnded)
          this.audioElement.removeEventListener('error', evts.onError)
          this.audioEvents = null
        }
        this.audioElement.pause()
        this.audioElement.src = ''
        this.audioElement.load()
        this.audioElement = null
      }
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl)
        this.audioUrl = ''
      }
    },

    clearAudio (event) {
      if (event) event.stopPropagation()
      this.clearAudioNoReset()
      this.selectedFile = null
      this.isPlaying = false
      this.currentTime = 0
      this.audioDuration = 0
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.deleteButtonClicked = false
      this.isProcessed = false
    },

    formatTime (seconds) {
      if (!seconds || isNaN(seconds)) return '00:00:00:000'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)
      const milliseconds = Math.floor((seconds % 1) * 1000)
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${milliseconds.toString().padStart(3, '0')}`
    },

    setTimeValidation (type, isValid, errorMessage) {
      if (type === 'start') {
        this.isStartTimeValid = isValid
        this.startTimeError = errorMessage
      } else {
        this.isEndTimeValid = isValid
        this.endTimeError = errorMessage
      }
    },

    validateTime (type) {
      const timeToValidate = type === 'start' ? this.cut_start_time : this.cut_end_time
      if (!timeToValidate?.trim()) {
        this.setTimeValidation(type, true, '')
        return
      }
      const trimmedTime = timeToValidate.trim()
      const timeRegex = /^(\d{1,2}):(\d{1,2}):(\d{1,2})(?::(\d{1,3}))?$/
      const match = trimmedTime.match(timeRegex)
      if (!match) {
        this.setTimeValidation(type, false, '请输入正确的时间格式 (HH:MM:SS:mmm 或 HH:MM:SS)')
        return
      }
      const hours = parseInt(match[1]) || 0
      const minutes = parseInt(match[2]) || 0
      const seconds = parseInt(match[3]) || 0
      const milliseconds = parseInt(match[4]) || 0
      if (minutes > 59 || seconds > 59) {
        this.setTimeValidation(type, false, '分钟和秒数不能超过59')
        return
      }
      const timeInSeconds = hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
      if (timeInSeconds > this.audioDuration) {
        this.setTimeValidation(type, false, `${type === 'start' ? '开始' : '结束'}时间不能超过音频总时长`)
        return
      }
      if (type === 'start' && this.cut_end_time && timeInSeconds >= this.timeToSeconds(this.cut_end_time)) {
        this.setTimeValidation(type, false, '开始时间必须小于结束时间')
        return
      }
      if (type === 'end' && this.cut_start_time && timeInSeconds <= this.timeToSeconds(this.cut_start_time)) {
        this.setTimeValidation(type, false, '结束时间必须大于开始时间')
        return
      }
      this.setTimeValidation(type, true, '')
      if (type === 'start') {
        this.cut_start_time = this.formatTime(timeInSeconds)
      } else {
        this.cut_end_time = this.formatTime(timeInSeconds)
      }
    },

    timeToSeconds (timeStr) {
      if (!timeStr) return 0
      const timeRegex = /^(\d{1,2}):(\d{1,2}):(\d{1,2})(?::(\d{1,3}))?$/
      const match = timeStr.match(timeRegex)
      if (!match) return 0
      const hours = parseInt(match[1]) || 0
      const minutes = parseInt(match[2]) || 0
      const seconds = parseInt(match[3]) || 0
      const milliseconds = parseInt(match[4]) || 0
      return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
    },

    toggleSection (section) {
      if (section === 'audioCut' && this.audioCutEnabled) {
        this.sections.audioCut = !this.sections.audioCut
      }
    },

    startProcessing () {
      this.showCostConfirm()
    },

    handleDeleteClick (event) {
      event.stopPropagation()
      this.deleteButtonClicked = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.clearAudio()
        }, 150)
      })
    },

    handleMouseLeave () {
      if (this.deleteButtonClicked) {
        setTimeout(() => {
          this.showDeleteButton = false
        }, 150)
      } else {
        this.showDeleteButton = false
      }
    }
  },
  beforeUnmount () {
    this.clearAudioNoReset()
  }
}
</script>

<style scoped>
/* Toast 样式 */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
  min-width: 280px;
  max-width: 500px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.toast-container.success { background-color: #10b981; color: white; }
.toast-container.error { background-color: #ef4444; color: white; }
.toast-container.warning { background-color: #f59e0b; color: white; }
.toast-container.info { background-color: #3b82f6; color: white; }

.toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toast-icon { font-size: 20px; font-weight: bold; }
.toast-message { flex: 1; font-size: 14px; }

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: linear-gradient(135deg, #2a2a2a 0%, #333 100%);
  border: 1px solid #00bcd4;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 188, 212, 0.3);
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #444;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  color: #00bcd4;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  color: #9ca3af;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: #444;
  color: white;
}

.modal-body {
  padding: 20px;
}

.cost-detail { margin-bottom: 16px; }

.cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
}

.service-name { color: #d1d5db; }
.service-price { color: #9ca3af; }

.cost-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #444;
  font-weight: 600;
  font-size: 16px;
}

.total-amount {
  color: #00bcd4;
  font-size: 18px;
}

.modal-tip {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px 20px;
  border-top: 1px solid #444;
}

.modal-btn {
  padding: 8px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.modal-btn-cancel {
  background-color: #4b5563;
  color: white;
}
.modal-btn-cancel:hover { background-color: #6b7280; }

.modal-btn-confirm {
  background-color: #00bcd4;
  color: white;
}
.modal-btn-confirm:hover { background-color: #0097a7; }

/* 原有样式保持不变 */
.main-content {
  height: calc(100vh - 3px);
  overflow-y: auto;
  padding-top: 10px;
  box-sizing: border-box;
}
.audio-processing-container {
  font-family: Arial, sans-serif;
  background-color: #1a1a1a;
  color: white;
  padding: 1rem 1.2rem;
  min-height: 480px;
}
.title {
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.6rem;
}
.content-flex {
  display: flex;
  justify-content: center;
  margin: 0 auto;
  max-width: 600px;
  width: 100%;
}
.main-area {
  width: 100%;
  padding: 0 0.1rem;
  margin-bottom: 1rem;
}
.audio-upload-area {
  position: relative;
  background-color: #2a2a2a;
  border-radius: 0.5rem;
  height: 8rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-bottom: 1rem;
}
.hidden-input { display: none; }
.delete-button {
  position: absolute;
  top: 0.15rem;
  right: 0.15rem;
  background-color: #3b82f6;
  border-radius: 9999px;
  width: 1.4rem;
  height: 1.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: none;
  cursor: pointer;
  z-index: 10;
  font-size: 1.2rem;
  font-weight: bold;
  transition: background-color 0.2s ease, transform 0.1s ease;
}
.delete-button:hover { background-color: #2563eb; }
.delete-button.clicked {
  background-color: #dc2626 !important;
  transform: scale(0.9);
}
.upload-icon {
  font-size: 2rem;
  color: #22d3ee;
  margin-bottom: 0.2rem;
}
.upload-text { text-align: center; margin-bottom: 0.5rem; }
.upload-subtext {
  text-align: center;
  font-size: 0.6rem;
  color: #9ca3af;
  margin-top: 0.3rem;
}
.audio-info {
  width: 100%;
  padding: 0 1.8rem;
}
.file-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.2rem;
}
.file-name {
  color: #d1d5db;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
  display: inline-block;
}
.time-info {
  display: flex;
  color: #d1d5db;
  gap: 1rem;
}
.progress-bar {
  width: 100%;
  background-color: #4b5563;
  height: 0.25rem;
  border-radius: 9999px;
  margin-bottom: 1rem;
}
.progress-fill {
  background-color: #22d3ee;
  height: 0.25rem;
  border-radius: 9999px;
}
.play-controls { display: flex; justify-content: center; }
.play-button {
  background-color: #22d3ee;
  color: white;
  border: none;
  padding: 0.2rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  white-space: nowrap;
}
.play-button:hover {
  background-color: #0891b2;
  transition: background-color 0.3s;
}
.function-selector {
  background-color: #2a2a2a;
  border-radius: 0.5rem;
  padding: 0.6rem 1rem;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.selector-label {
  color: #d1d5db;
  font-size: 0.8rem;
  white-space: nowrap;
}
.function-select {
  flex: 1;
  background-color: #4b5563;
  border: none;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  cursor: pointer;
}
.feature-section {
  background-color: #2a2a2a;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.6rem;
}
.feature-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  user-select: none;
}
.feature-title { display: flex; align-items: center; }
.feature-checkbox {
  margin-right: 0.75rem;
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}
.feature-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.feature-content {
  padding: 0.75rem 1rem;
  border-top: 1px solid #4b5563;
}
.no-border { border-top: none; }
.option-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.option-row:last-child { margin-bottom: 0; }
.option-half {
  width: 50%;
  display: flex;
  align-items: center;
}
.option-full {
  width: 100%;
  display: flex;
  align-items: center;
}
.option-checkbox { margin-right: 0.5rem; }
.option-relative { position: relative; }
.option-input, .option-select {
  width: 80%;
  background-color: #4b5563;
  border: none;
  color: white;
  padding: 0.35rem 0.55rem;
  border-radius: 0.25rem;
}
.option-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.error-message {
  color: #ef4444;
  font-size: 0.75rem;
  position: absolute;
  bottom: -1.25rem;
  left: 0;
}
.slider-row {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}
.slider-row:last-child { margin-bottom: 0; }
.slider-label { width: 4rem; }
.slider-input { flex: 1; margin: 0 0.5rem; }
.slider-value {
  width: 4rem;
  text-align: right;
}
.radio-options {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}
.radio-label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.radio-input { margin-right: 0.5rem; }

/* 新增：下拉三角箭头样式 */
.toggle-arrow {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 0.5rem;
  transition: transform 0.2s ease;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
}
.toggle-arrow:hover {
  color: #22d3ee;
}

.process-button {
  width: 100%;
  padding: 0.5rem;
  background-color: #22d3ee;
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  margin-bottom: 1rem;
}
.process-button:hover {
  background-color: #0891b2;
  transition: background-color 0.3s;
}
.process-button.disabled {
  background-color: #4b5563;
  cursor: not-allowed;
}
.terms-text {
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
}
input[type="range"] {
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  background: #4b5563;
  border-radius: 3px;
  outline: none;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #22d3ee;
  cursor: pointer;
}
input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #22d3ee;
  cursor: pointer;
  border: none;
}
@media (max-width: 768px) {
  .content-flex {
    flex-direction: column;
    align-items: center;
  }
  .main-area { max-width: 100%; }
}
</style>
